import React, { useState } from "react";
import { Small, H2, Input, Button, P } from "@upsolve/ui";
import { ModalClose, ViewportCenteredFixed } from "../modals/Modal";
import { StyledOverlayBackdrop } from "../modals/StyledOverlayBackdrop";
import { ScreenerModal, ScreenerModalBody } from "../modals/ScreenerModal";

type Step = "services" | "comingSoon";
type SubService =
  | "creditCard"
  | "personalLoan"
  | "mortgage"
  | "carLoan"
  | "medicalDebt"
  | "taxDebt"
  | "somethingElse";
type MainService =
  | "stopCollection"
  | "debtDispute"
  | "settleDebt"
  | "talkToLawyer"
  | "exploreBankruptcy";

type OnHideFunction = (...args: any[]) => void;

interface ServiceInfo {
  url: string;
  message: string | React.ReactNode;
}

interface ProductSelectorModalProps {
  onHide: OnHideFunction;
}

const serviceMapping: Record<
  SubService,
  ServiceInfo | Partial<Record<MainService, ServiceInfo>>
> = {
  creditCard: {
    debtDispute: {
      url: "https://upsolve.org/learn/dispute-debt/",
      message:
        "There are powerful ways for you to dispute credit card debt, and we’ve created free resources to guide you.",
    },
    stopCollection: {
      url: "https://upsolve.org/learn/stop-collection-calls",
      message:
        "There are powerful ways for you to stop credit card collection calls, and we’ve created free resources to guide you.",
    },
    settleDebt: {
      url: "https://upsolve.org/learn/negotiating-with-debt-collectors/",
      message:
        "There are powerful ways for you to negotiate with credit card creditors and debt collectors, and we’ve created free resources to guide you.",
    },
  },
  personalLoan: {
    debtDispute: {
      url: "https://upsolve.org/learn/dispute-debt/",
      message:
        "There are powerful ways for you to dispute personal loan debt, and we’ve created free resources to guide you.",
    },
    stopCollection: {
      url: "https://upsolve.org/learn/stop-collection-calls",
      message:
        "There are powerful ways for you to stop personal loan collection calls, and we’ve created free resources to guide you.",
    },
    settleDebt: {
      url: "https://upsolve.org/learn/negotiating-with-debt-collectors/",
      message:
        "There are powerful ways for you to negotiate with personal loan creditors and debt collectors, and we’ve created free resources to guide you.",
    },
  },
  carLoan: {
    url: "https://upsolve.org/learn/cant-afford-your-car-payment/",
    message:
      "There are several ways for you to get relief from your car loan, and we’ve created free resources to guide you.",
  },
  taxDebt: {
    url: "https://upsolve.org/learn/offer-in-compromise/",
    message:
      "You may be able to reduce or eliminate your Tax Debt by negotiating with the IRS through a process called “Offer in Compromise.”",
  },
  medicalDebt: {
    url: "https://upsolve.org/learn/getting-out-of-medical-debt/",
    message:
      "There are several ways for you to get relief from your medical debt, and we’ve created free resources to guide you.",
  },
  mortgage: {
    url: "https://upsolve.org/learn/missed-mortgage-payments/",
    message:
      "There are several ways for you to get relief from your mortgage, and we’ve created free resources to guide you.",
  },
  somethingElse: {
    url: "https://upsolve.org/learn/what-is-debt-and-how-to-handle-it/",
    message: (
      <P>
        In the meantime, You can learn more about dealing with debt in{" "}
        <a href="https://upsolve.org/learn/what-is-debt-and-how-to-handle-it/">
          this article
        </a>
        .
      </P>
    ),
  },
};

const serviceHeaders: Record<MainService, string> = {
  stopCollection: "What type of debt are the collection calls about?",
  debtDispute: "What type of debt do you want to dispute?",
  settleDebt: "What type of debt do you want to negotiate?",
  talkToLawyer: "What type of debt do you want to talk with a lawyer about?",
  exploreBankruptcy: "What aspect of bankruptcy do you want to explore?",
};

export const ProductSelectorModal: React.FC<ProductSelectorModalProps> = ({
  onHide,
}) => {
  const [step, setStep] = useState<Step | null>();
  const [mainService, setMainService] = useState<MainService | null>();
  const [subService, setSubService] = useState<SubService | null>();

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const handleEmailChange = (emailEvent: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = emailEvent.target;
    setEmail(value);
    // Validate email format
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    setError(isValidEmail ? "" : "Please enter a valid email address");
  };

  const handleClose = () => {
    setEmail(""); // Clear email field
    setError(""); // Clear error message
    onHide(false);
  };

  const handleServiceNavigation = (selectedSubService: SubService) => {
    setSubService(selectedSubService);
    if (!mainService) {
      return;
    }

    const serviceInfo =
      (serviceMapping[selectedSubService] as Partial<Record<MainService, ServiceInfo>>)[mainService]
      || serviceMapping[selectedSubService];
    if (serviceInfo) {
      setStep("comingSoon");
    } else {
      setStep(null);
    }
  };

  const handleURLButton = (url: string) => {
    window.open(url, "_blank");
  };

  const renderStep = () => {
    switch (step) {
      case "services":
        if (!mainService) {
          setStep(null);
          return;
        }

        const serviceHeader = serviceHeaders[mainService];

        return (
          <div style={{ textAlign: "center" }}>
            {serviceHeader && (
              <H2 style={{ fontFamily: "Castoro" }}>{serviceHeader}</H2>
            )}
            <P>This tool can help you with one debt at a time.</P>
            <div style={{ marginTop: "20px" }}>
              <Button onClick={() => handleServiceNavigation("creditCard")} style={{ marginBottom: "10px" }}>
                Credit Card
              </Button>
              <Button inverted onClick={() => handleServiceNavigation("personalLoan")} style={{ marginBottom: "10px" }}>
                Personal Loan
              </Button>
              <Button inverted onClick={() => handleServiceNavigation("mortgage")} style={{ marginBottom: "10px" }}>
                Mortgage
              </Button>
              <Button inverted onClick={() => handleServiceNavigation("carLoan")} style={{ marginBottom: "10px" }}>
                Car Loan
              </Button>
              <Button inverted onClick={() => handleServiceNavigation("medicalDebt")} style={{ marginBottom: "10px" }}>
                Medical Debt
              </Button>
              <Button inverted onClick={() => handleServiceNavigation("taxDebt")} style={{ marginBottom: "10px" }}>
                Tax Debt
              </Button>
              <Button inverted onClick={() => handleServiceNavigation("somethingElse")} style={{ marginBottom: "10px" }}>
                Something else
              </Button>
            </div>
          </div>
        );
      case "comingSoon":
        if (!subService || !mainService) {
          setStep(null);
          return;
        }

        const serviceInfo =
          (serviceMapping[subService] as Partial<Record<MainService, ServiceInfo>>)[mainService]
          || serviceMapping[subService];

        // Check if serviceInfo is of type ServiceInfo. If not we will render the talkToLawyer page.
        if ('url' in serviceInfo && 'message' in serviceInfo) {
          return (
            <div>
              <H2 style={{ fontFamily: "Castoro" }}>Coming Soon!</H2>
              <P>
                If you provide your email address, we’ll let you know as soon as
                this tool is ready.
              </P>

              <Small>Optional Email address</Small>
              <Input
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={handleEmailChange}
              />
              {error && email && (
                <Small style={{ color: "red" }}>{error}</Small>
              )}

              <p>
                If you’d like to speak with a lawyer, you can{" "}
                <a
                   href={
                    email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                      ? "https://upsolve.org/find-a-lawyer/?email=" + email
                      : "https://upsolve.org/find-a-lawyer/"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  request a free bankruptcy evaluation
                </a>{" "}
                from a law firm in your state.
              </p>

              <P>{serviceInfo.message}</P>

              <Button
                onClick={() => handleURLButton(serviceInfo.url)}
                disabled={!!error && !!email}
              >
                Learn More
              </Button>
            </div>
          );
        }
        return (
          <div>
            <H2 style={{ fontFamily: 'Castoro' }}>Expert Help</H2>
            <p>If you’d like to speak with a lawyer about your debt, you can request
               a free bankruptcy evaluation from a law firm in your state.
            </p>

            <Button onClick={() => handleURLButton("https://upsolve.org/find-a-lawyer")}>
            Request free evaluation
            </Button>
          </div>
        );
      default:
        return (
          <div style={{ textAlign: "center" }}>
            <H2 style={{ fontFamily: "Castoro" }}>
              What can we help you do today?
            </H2>
            <div style={{ marginTop: "20px" }}>
              <Button
                onClick={() => handleURLButton("https://my.upsolve.org/intro")}
                style={{ marginBottom: "10px" }}
              >
                Explore bankruptcy
              </Button>
              <Button
                inverted
                onClick={() => handleServiceSelection("stopCollection")}
                style={{ marginBottom: "10px" }}
              >
                Stop debt collection calls
              </Button>
              <Button
                inverted
                onClick={() => handleServiceSelection("settleDebt")}
                style={{ marginBottom: "10px" }}
              >
                Negotiate with a debt collector
              </Button>
              <Button
                inverted
                onClick={() => handleServiceSelection("debtDispute")}
                style={{ marginBottom: "10px" }}
              >
                Dispute a debt
              </Button>
              <Button
                inverted
                onClick={() => handleServiceSelection("talkToLawyer")}
                style={{ marginBottom: "10px" }}
              >
                Talk to a lawyer about a debt
              </Button>
            </div>
          </div>
        );
    }
  };

  const handleServiceSelection = (selectedService: MainService) => {
    setMainService(selectedService);
    setStep("services");
  };

  return (
    <>
      <ViewportCenteredFixed>
        <ScreenerModal>
          <ModalClose onClick={handleClose} />
          <ScreenerModalBody>{renderStep()}</ScreenerModalBody>
        </ScreenerModal>
      </ViewportCenteredFixed>
      <StyledOverlayBackdrop onClick={handleClose} />
    </>
  );
};
